import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'core/modules/atoms/Button';
import Loading from 'core/modules/atoms/Loading';
import Drawer from 'core/modules/components/Drawer/Drawer';
import ChatMessages from '../../components/atoms/ChatMessages/ChatMessages';
import type { AnnotationStatus, FlaggedMessage } from '../TeacherAIAnalyticsPanelView/sections/InappropriateUseSection/useInappropriateUseSection';
import './_student-conversation-drawer.scss';
import useStudentConversationDrawer from './useStudentConversationDrawer';

type StudentConversationDrawerProps = {
  message: FlaggedMessage | null;
  onClose: () => void;
  onStatusChanged?: (annotationId: string, newStatus: AnnotationStatus, reviewerName: string) => void;
  open: boolean;
};

function getInitials(name: string, lastName: string): string {
  const firstInitial = name?.charAt(0)?.toUpperCase() || '';
  const lastInitial = lastName?.charAt(0)?.toUpperCase() || '';
  return `${firstInitial}${lastInitial}`;
}

function formatDrawerDate(dateString: string): string {
  const date = new Date(dateString);
  const day = date.getDate();
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year} - ${hours}:${minutes}`;
}

function StudentConversationDrawer({ message, onClose, onStatusChanged, open }: StudentConversationDrawerProps): JSX.Element {
  const { t } = useTranslation(['chatbot']);
  const contentRef = useRef<HTMLDivElement>(null);

  const { conversation, error, handleDismiss, handleMarkAsAttended, loading, updating } = useStudentConversationDrawer({
    conversationId: message?.conversationId ?? null,
    message,
    onStatusChanged,
    open,
  });

  const scrollToFlaggedMessage = useCallback(() => {
    if (!message?.messageId || !contentRef.current) return;

    const flaggedElement = contentRef.current.querySelector(`[data-message-id="${message.messageId}"]`);
    if (flaggedElement) {
      flaggedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [message?.messageId]);

  useEffect(() => {
    if (open && conversation.length > 0) {
      const timer = setTimeout(scrollToFlaggedMessage, 100);
      return () => clearTimeout(timer);
    }
  }, [open, conversation, scrollToFlaggedMessage]);

  const enrichedConversation = useMemo(() => {
    if (!message?.messageId || !conversation.length) return conversation;
    return conversation.map((msg) => {
      if (msg.id === message.messageId) {
        return { ...msg, highlighted: true, flagCategories: message.categories };
      }
      return msg;
    });
  }, [conversation, message]);

  const renderHeader = (): JSX.Element | null => {
    if (!message) return null;

    return (
      <div className="student-conversation-drawer__header">
        <div className="student-conversation-drawer__header-left">
          <div className="student-conversation-drawer__avatar">{getInitials(message.studentName, message.studentLastName)}</div>
          <div className="student-conversation-drawer__header-info">
            <span className="student-conversation-drawer__student-name">
              {message.studentName} {message.studentLastName}
            </span>
            <span className="student-conversation-drawer__subtitle">
              {formatDrawerDate(message.createdAt)}
              {message.groupName && (
                <>
                  <span className="student-conversation-drawer__subtitle-separator">&bull;</span>
                  {message.groupName}
                </>
              )}
            </span>
          </div>
        </div>
        <button aria-label={t('chatbot:Close')} className="student-conversation-drawer__close-button" onClick={onClose} type="button">
          <svg fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
          </svg>
        </button>
      </div>
    );
  };

  const renderFooter = (): JSX.Element | null => {
    if (!message) return null;

    if (message.annotationStatus === 'PENDING') {
      return (
        <div className="student-conversation-drawer__footer">
          <Button
            color="primary"
            disabled={updating}
            onClick={handleMarkAsAttended}
            size="xs"
            text={t('chatbot:ai_analytics_student_conversation_mark_attended')}
            type="button"
          />
          <Button
            color="primary-outlined"
            disabled={updating}
            onClick={handleDismiss}
            size="xs"
            text={t('chatbot:ai_analytics_student_conversation_dismiss')}
            type="button"
            variant="outline"
          />
        </div>
      );
    }

    const isAttended = message.annotationStatus === 'REVIEWED';
    const infoText = isAttended
      ? t('chatbot:ai_analytics_student_conversation_alert_attended_by')
      : t('chatbot:ai_analytics_student_conversation_alert_dismissed_by');
    const badgeText = isAttended ? t('chatbot:ai_analytics_inappropriate_use_status_attended') : t('chatbot:ai_analytics_inappropriate_use_status_dismissed');

    return (
      <div className="student-conversation-drawer__footer student-conversation-drawer__footer--resolved">
        <p className="student-conversation-drawer__footer-info">
          {infoText} <strong>{message.reviewerName}</strong>
        </p>
        <div className={`student-conversation-drawer__footer-badge student-conversation-drawer__footer-badge--${isAttended ? 'attended' : 'dismissed'}`}>
          {isAttended && (
            <svg fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.667 5L7.5 14.167 3.333 10" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
            </svg>
          )}
          {badgeText}
        </div>
      </div>
    );
  };

  const renderContent = (): JSX.Element => {
    if (loading) {
      return (
        <div className="student-conversation-drawer__loading">
          <Loading />
        </div>
      );
    }

    if (error) {
      return (
        <div className="student-conversation-drawer__error">
          <p>{t('chatbot:ai_analytics_student_conversation_error')}</p>
        </div>
      );
    }

    if (conversation.length === 0) {
      return (
        <div className="student-conversation-drawer__empty">
          <p>{t('chatbot:ai_analytics_student_conversation_empty')}</p>
        </div>
      );
    }

    return (
      <div className="student-conversation-drawer__messages" ref={contentRef}>
        <ChatMessages messages={enrichedConversation} mode="read-only" readOnly />
      </div>
    );
  };

  return (
    <Drawer className="student-conversation-drawer" footer={renderFooter()} header={renderHeader()} onClose={onClose} open={open} width={490}>
      {renderContent()}
    </Drawer>
  );
}

export default StudentConversationDrawer;
